import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { formatCurrency } from '../../../utils/formatters';
import ComboFiltro from '../../../components/ComboFiltro';
import BadgeFlussoCommerciale from '../../../components/BadgeFlussoCommerciale';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { API_DOCS } from './OrdiniPage.api';
import './OrdiniPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoOrdine = 'BOZZA' | 'CONFERMATO' | 'IN_LAVORAZIONE' | 'CHIUSO';
type ModalitaEvasione = 'SAL' | 'DDT' | 'RAPPORTINO' | 'MISTA';
type DestinatarioOrdine = 'IMPRESA' | 'ACQUIRENTE';
type FlussoCommerciale = 'VENDITA' | 'APPALTO_CONTRATTO';

interface OrdineItem {
  id: number;
  numero: string;
  societaId: number;
  commessaId: number;
  dataOrdine: string;
  stato: StatoOrdine;
  flussoCommerciale: FlussoCommerciale | null;
  modalitaEvasione: ModalitaEvasione | null;
  destinatarioOrdine: DestinatarioOrdine | null;
  acquirenteId: number | null;
  totale: string | null;
  preventivoOrigineId: number | null;
  commessa: { id: number; numero: string; descrizione: string | null; clienteRagioneSociale: string | null } | null;
  acquirente: { id: number; nome: string | null; cognome: string | null; unitaCodice: string | null } | null;
  preventivoOrigine: { id: number; numero: string } | null;
  // Righe ancora da lavorare (evidenza in lista)
  righeDaConfigurare: number;
  righeDaEsplodere: number;
  righeDaTrasformare: number;
  righeDaLavorare: number;
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

interface AcquirenteOption {
  id: number;
  unitaCodice: string | null;
  nome: string | null;
  cognome: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoOrdine, string> = {
  BOZZA: 'Bozza',
  CONFERMATO: 'Confermato',
  IN_LAVORAZIONE: 'In lavorazione',
  CHIUSO: 'Chiuso',
};

const STATO_CLS: Record<StatoOrdine, string> = {
  BOZZA: 'ord-badge-stato--bozza',
  CONFERMATO: 'ord-badge-stato--confermato',
  IN_LAVORAZIONE: 'ord-badge-stato--in-lavorazione',
  CHIUSO: 'ord-badge-stato--chiuso',
};

const MODALITA_LABEL: Record<ModalitaEvasione, string> = {
  SAL: 'SAL',
  DDT: 'DDT',
  RAPPORTINO: 'Rapportino',
  MISTA: 'Mista',
};

const DESTINATARIO_LABEL: Record<DestinatarioOrdine, string> = {
  IMPRESA: 'Impresa',
  ACQUIRENTE: 'Acquirente',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function renderCliente(o: OrdineItem): React.ReactNode {
  const cliente = o.commessa?.clienteRagioneSociale || null;
  const acquirenteNome = o.acquirente
    ? `${o.acquirente.nome || ''} ${o.acquirente.cognome || ''}`.trim()
    : '';
  const acquirenteUnita = o.acquirente?.unitaCodice || null;

  if (!cliente && !o.acquirente) {
    return <span className="bop-text-muted">—</span>;
  }

  return (
    <div className="ord-cliente-cell">
      <span className="ord-cliente-cell__cliente">
        {cliente || <span className="bop-text-muted">—</span>}
      </span>
      {o.acquirente && (
        <span className="ord-cliente-cell__acquirente">
          {acquirenteNome || '—'}
          {acquirenteUnita && <span className="bop-text-muted"> • {acquirenteUnita}</span>}
        </span>
      )}
    </div>
  );
}

function renderDaLavorare(o: OrdineItem): React.ReactNode {
  const badges: React.ReactNode[] = [];
  if (o.righeDaConfigurare > 0) {
    badges.push(
      <span
        key="cfg"
        className="ord-badge-lavoro ord-badge-lavoro--configurare"
        title={`${o.righeDaConfigurare} righe da configurare`}
      >
        ⚙ {o.righeDaConfigurare} da configurare
      </span>
    );
  }
  if (o.righeDaEsplodere > 0) {
    badges.push(
      <span
        key="esp"
        className="ord-badge-lavoro ord-badge-lavoro--esplodere"
        title={`${o.righeDaEsplodere} righe a corpo da esplodere`}
      >
        🧩 {o.righeDaEsplodere} da esplodere
      </span>
    );
  }
  if (o.righeDaTrasformare > 0) {
    badges.push(
      <span
        key="trf"
        className="ord-badge-lavoro ord-badge-lavoro--trasformare"
        title={`${o.righeDaTrasformare} righe da trasformare in contratto`}
      >
        📄 {o.righeDaTrasformare} da trasformare
      </span>
    );
  }
  if (badges.length === 0) {
    return <span className="bop-text-muted">—</span>;
  }
  return <div className="ord-lavoro-cell">{badges}</div>;
}

function clienteText(o: OrdineItem): string {
  const c = o.commessa?.clienteRagioneSociale || '';
  const a = o.acquirente
    ? `${o.acquirente.nome || ''} ${o.acquirente.cognome || ''} ${o.acquirente.unitaCodice || ''}`.trim()
    : '';
  return `${c} ${a}`.trim();
}

function renderOrdine(o: OrdineItem): React.ReactNode {
  return (
    <div className="ord-ordine-cell">
      <span className="ord-numero-link">{o.numero}</span>
      <span className="ord-ordine-cell__meta">
        <BadgeFlussoCommerciale flusso={o.flussoCommerciale} compatto />
        <span className="ord-ordine-cell__data">{formatDateIT(o.dataOrdine)}</span>
      </span>
    </div>
  );
}

function renderCommessa(o: OrdineItem, navigate: (path: string) => void): React.ReactNode {
  return (
    <div className="ord-commessa-cell2">
      {o.commessa ? (
        <span className="ord-commessa-cell2__main">
          <span className="ord-commessa-link">{o.commessa.numero}</span>
          {o.commessa.descrizione && (
            <span className="bop-text-muted ord-commessa-desc"> — {o.commessa.descrizione}</span>
          )}
        </span>
      ) : (
        <span className="bop-text-muted">—</span>
      )}
      {o.preventivoOrigine && (
        <span
          className="ord-preventivo-link"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/commerciale/attivo/preventivi/${o.preventivoOrigine!.id}`);
          }}
          title="Apri preventivo di origine"
        >
          Prev. {o.preventivoOrigine.numero}
        </span>
      )}
    </div>
  );
}

function buildOrdiniColumns(navigate: (path: string) => void): BopExcelColumn<OrdineItem>[] {
  return [
    {
      key: 'numero',
      header: 'Ordine',
      accessor: (o) => o.numero,
      filter: { type: 'text' },
      width: '160px',
      hideable: false,
      render: (o) => renderOrdine(o),
    },
    {
      key: 'dataOrdine',
      header: 'Data',
      accessor: (o) => o.dataOrdine || '',
      filter: { type: 'dateRange' },
      width: '110px',
      defaultHidden: true,
      render: (o) => formatDateIT(o.dataOrdine),
    },
    {
      key: 'flussoCommerciale',
      header: 'Flusso',
      accessor: (o) => o.flussoCommerciale || '',
      filter: {
        type: 'enum',
        options: [
          { value: 'VENDITA',           label: 'Vendita' },
          { value: 'APPALTO_CONTRATTO', label: 'Appalto / Contratto' },
        ],
      },
      width: '130px',
      defaultHidden: true,
      render: (o) => <BadgeFlussoCommerciale flusso={o.flussoCommerciale} compatto />,
    },
    {
      key: 'commessa',
      header: 'Commessa / Preventivo',
      accessor: (o) => o.commessa?.numero || '',
      filter: { type: 'text' },
      render: (o) => renderCommessa(o, navigate),
    },
    {
      key: 'cliente',
      header: 'Cliente / Acquirente',
      accessor: clienteText,
      filter: { type: 'text' },
      render: (o) => renderCliente(o),
    },
    {
      key: 'destinatarioOrdine',
      header: 'Destinatario',
      accessor: (o) => o.destinatarioOrdine || '',
      filter: {
        type: 'enum',
        options: [
          { value: 'IMPRESA',    label: 'Impresa' },
          { value: 'ACQUIRENTE', label: 'Acquirente' },
        ],
      },
      width: '120px',
      render: (o) => o.destinatarioOrdine
        ? DESTINATARIO_LABEL[o.destinatarioOrdine]
        : <span className="bop-text-muted">—</span>,
    },
    {
      key: 'modalitaEvasione',
      header: 'Modalità',
      accessor: (o) => o.modalitaEvasione || '',
      filter: {
        type: 'enum',
        options: [
          { value: 'SAL',        label: 'SAL' },
          { value: 'DDT',        label: 'DDT' },
          { value: 'RAPPORTINO', label: 'Rapportino' },
          { value: 'MISTA',      label: 'Mista' },
        ],
      },
      width: '110px',
      defaultHidden: true,
      render: (o) => o.modalitaEvasione
        ? MODALITA_LABEL[o.modalitaEvasione]
        : <span className="bop-text-muted">—</span>,
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (o) => o.stato,
      filter: {
        type: 'enum',
        options: [
          { value: 'BOZZA',          label: 'Bozza' },
          { value: 'CONFERMATO',     label: 'Confermato' },
          { value: 'IN_LAVORAZIONE', label: 'In lavorazione' },
          { value: 'CHIUSO',         label: 'Chiuso' },
        ],
      },
      width: '130px',
      render: (o) => (
        <span className={`ord-badge-stato ${STATO_CLS[o.stato]}`}>
          {STATO_LABEL[o.stato]}
        </span>
      ),
    },
    {
      key: 'daLavorare',
      header: 'Da lavorare',
      accessor: (o) => ((o.righeDaLavorare ?? 0) > 0 ? 'si' : 'no'),
      filter: {
        type: 'enum',
        options: [
          { value: 'si', label: 'Con righe da lavorare' },
          { value: 'no', label: 'Nessuna' },
        ],
      },
      width: '170px',
      render: (o) => renderDaLavorare(o),
    },
    {
      key: 'totale',
      header: 'Totale',
      accessor: (o) => o.totale != null ? Number(o.totale) : null,
      filter: { type: 'number' },
      width: '120px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right ord-totale',
      render: (o) => o.totale != null
        ? formatCurrency(Number(o.totale))
        : <span className="bop-text-muted">—</span>,
    },
  ];
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function OrdiniPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { isSuperadmin } = usePermesso();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri (inizializzati da URL)
  const [stato, setStato] = useState<'' | StatoOrdine>(
    (searchParams.get('stato') as '' | StatoOrdine) || ''
  );
  const [flussoCommerciale, setFlussoCommerciale] = useState<'' | FlussoCommerciale>(
    (searchParams.get('flussoCommerciale') as '' | FlussoCommerciale) || ''
  );
  const [destinatario, setDestinatario] = useState<'' | DestinatarioOrdine>(
    (searchParams.get('destinatarioOrdine') as '' | DestinatarioOrdine) || ''
  );
  const [commessaId, setCommessaId] = useState(searchParams.get('commessaId') ?? '');
  const [acquirenteId, setAcquirenteId] = useState(searchParams.get('acquirenteId') ?? '');
  const [dataDa, setDataDa] = useState(searchParams.get('dataDa') ?? '');
  const [dataA, setDataA] = useState(searchParams.get('dataA') ?? '');
  const [qInput, setQInput] = useState(searchParams.get('q') ?? '');
  const [q, setQ] = useState(searchParams.get('q') ?? '');

  // Dati
  const [items, setItems] = useState<OrdineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);
  const [acquirentiOpts, setAcquirentiOpts] = useState<AcquirenteOption[]>([]);

  // Debounce ricerca testo
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (stato) params.set('stato', stato);
      if (flussoCommerciale) params.set('flussoCommerciale', flussoCommerciale);
      if (destinatario) params.set('destinatarioOrdine', destinatario);
      if (commessaId) params.set('commessaId', commessaId);
      if (acquirenteId) params.set('acquirenteId', acquirenteId);
      if (dataDa) params.set('dataDa', dataDa);
      if (dataA) params.set('dataA', dataA);
      if (q) params.set('q', q);

      // @api GET /api/ordini?societaId={id}&stato={stato}&flussoCommerciale={flusso}&destinatarioOrdine={d}&commessaId={cid}&acquirenteId={aid}&dataDa={d}&dataA={d}&q={q} -> {"success":true,"data":[{"id":"number","numero":"string","societaId":"number","commessaId":"number","dataOrdine":"string","stato":"string","flussoCommerciale":"string|null","modalitaEvasione":"string|null","destinatarioOrdine":"string|null","acquirenteId":"number|null","totale":"string","preventivoOrigineId":"number|null","commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"acquirente":{"id":"number","nome":"string","cognome":"string","unitaCodice":"string"},"preventivoOrigine":{"id":"number","numero":"string"},"righeDaConfigurare":"number","righeDaEsplodere":"number","righeDaTrasformare":"number","righeDaLavorare":"number"}]}
      const r = await apiFetch<{ success: boolean; data: OrdineItem[] }>(
        `/api/ordini?${params.toString()}`
      );
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nel caricamento', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, stato, flussoCommerciale, destinatario, commessaId, acquirenteId, dataDa, dataA, q]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Sincronizza URL con i filtri
  useEffect(() => {
    const sp = new URLSearchParams();
    if (stato) sp.set('stato', stato);
    if (flussoCommerciale) sp.set('flussoCommerciale', flussoCommerciale);
    if (destinatario) sp.set('destinatarioOrdine', destinatario);
    if (commessaId) sp.set('commessaId', commessaId);
    if (acquirenteId) sp.set('acquirenteId', acquirenteId);
    if (dataDa) sp.set('dataDa', dataDa);
    if (dataA) sp.set('dataA', dataA);
    if (q) sp.set('q', q);
    setSearchParams(sp, { replace: true });
  }, [stato, flussoCommerciale, destinatario, commessaId, acquirenteId, dataDa, dataA, q, setSearchParams]);

  // Carica commesse per il combo (cambia con societaId effettiva)
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Carica acquirenti della commessa selezionata
  useEffect(() => {
    if (!commessaId) {
      setAcquirentiOpts([]);
      // Resetta selezione acquirente se la commessa cambia
      setAcquirenteId(prev => (prev ? '' : prev));
      return;
    }
    // @api GET /api/commesse/:id/acquirenti -> {"success":true,"data":[{"id":"number","unitaCodice":"string","nome":"string","cognome":"string"}]}
    apiFetch<{ success: boolean; data: AcquirenteOption[] }>(`/api/commesse/${commessaId}/acquirenti`)
      .then(r => setAcquirentiOpts(r.data || []))
      .catch(() => setAcquirentiOpts([]));
  }, [commessaId]);

  const pulisciFiltri = () => {
    setStato('');
    setFlussoCommerciale('');
    setDestinatario('');
    setCommessaId('');
    setAcquirenteId('');
    setDataDa('');
    setDataA('');
    setQInput('');
    setQ('');
  };

  // Opzioni per ComboFiltro (id stringa)
  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));
  const acquirentiComboItems = acquirentiOpts.map(a => ({
    id: String(a.id),
    nome: `${a.unitaCodice || ''} ${a.nome || ''} ${a.cognome || ''}`.trim() || `#${a.id}`,
  }));

  const apriDettaglio = (id: number) => {
    navigate(`/commerciale/attivo/ordini/${id}`);
  };

  const columns = useMemo(() => buildOrdiniColumns(navigate), [navigate]);

  return (
    <Layout>
      <div className="ord-page">
        {/* Header — niente bottone "Nuovo ordine": creazione solo via conversione preventivo (Q4) */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Ordini Clienti</h1>
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar ord-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input ord-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Numero, descrizione, cliente..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select
              className="bop-select"
              value={stato}
              onChange={e => setStato(e.target.value as '' | StatoOrdine)}
            >
              <option value="">Tutti</option>
              <option value="BOZZA">Bozza</option>
              <option value="CONFERMATO">Confermato</option>
              <option value="IN_LAVORAZIONE">In lavorazione</option>
              <option value="CHIUSO">Chiuso</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Flusso commerciale</label>
            <select
              className="bop-select"
              value={flussoCommerciale}
              onChange={e => setFlussoCommerciale(e.target.value as '' | FlussoCommerciale)}
            >
              <option value="">Tutti</option>
              <option value="VENDITA">📦 Vendita</option>
              <option value="APPALTO_CONTRATTO">🏗️ Appalto/Contratto</option>
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Destinatario</label>
            <select
              className="bop-select"
              value={destinatario}
              onChange={e => setDestinatario(e.target.value as '' | DestinatarioOrdine)}
            >
              <option value="">Tutti</option>
              <option value="IMPRESA">Impresa</option>
              <option value="ACQUIRENTE">Acquirente</option>
            </select>
          </div>

          <div className="bop-form-group ord-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group ord-combo-group">
            <label className="bop-label">Acquirente</label>
            <ComboFiltro
              label="acquirente"
              value={acquirenteId || null}
              onChange={id => setAcquirenteId(id || '')}
              items={acquirentiComboItems}
              disabled={!commessaId}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Da</label>
            <input
              type="date"
              className="bop-input"
              value={dataDa}
              onChange={e => setDataDa(e.target.value)}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">A</label>
            <input
              type="date"
              className="bop-input"
              value={dataA}
              onChange={e => setDataA(e.target.value)}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella Excel-like */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare gli ordini</div>
          </div>
        ) : (
          <BopExcelTable<OrdineItem>
            data={items}
            loading={loading}
            rowKey={(o) => o.id}
            onRowClick={(o) => apriDettaglio(o.id)}
            initialSort={{ key: 'dataOrdine', dir: 'desc' }}
            storageKey="ordini-clienti"
            emptyState={
              <div className="ord-empty">
                <svg className="ord-empty__icon" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                <h3 className="ord-empty__title">Nessun ordine trovato</h3>
                <p className="ord-empty__text">Nessun ordine trovato con i filtri attuali.</p>
              </div>
            }
            columns={columns}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
